import React, { useMemo } from 'react';
import { Button } from '@mui/material';
import ReservationConstants from '../../../constants/ReservationConstants';
import MaterialDesignDialog from '../../general/MaterialDesignDialog.react';
import ReservationActions from '../../../actions/ReservationActions';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import Analytics from 'arbitrip-common/client/analytics';
import { POPUP_NAME, POPUP_CHOICE } from 'arbitrip-common/client/analytics/constants/interactions';

const CancelReservationDialog = ({ open, onClose, booking, profile, in_cancel_policy, points }) => {
	const { t, i18n } = useTranslation();

	if (!booking) {
		return null;
	}

	const closeCancelModal = () => {
		// TODO: why do we need this: because it close modal on 'OK' button click when cancellation failed
		ReservationActions.resetReservationCancellationStatus(booking.id);
		onClose();
	};

	const isConfirmPromptShown =
		booking.status !== ReservationConstants.RESERVATION_STATUS.CANCELING &&
		booking.cancelStatus !== ReservationConstants.STATUS.FAILED;

	const trackPopupChoice = (choice) => {
		Analytics.actions.interactions.popupClicked(profile, POPUP_NAME.CANCEL_BOOKING, choice);
	};

	const takeMeBack = () => {
		trackPopupChoice(POPUP_CHOICE.CANCEL);
		closeCancelModal();
	};

	const dismissDialog = (...args) => {
		if (isConfirmPromptShown) {
			trackPopupChoice(POPUP_CHOICE.CLOSE);
		}
		onClose(...args);
	};

	const cancelBooking = () => {
		trackPopupChoice(POPUP_CHOICE.CONFIRM);
		console.log('Canceling booking: ', booking);
		ReservationActions.cancelReservation(profile, booking, points);

		onClose();
	};

	const getFooter = () => {
		if (booking.status !== ReservationConstants.RESERVATION_STATUS.CANCELING) {
			return (
				<div className="cancel-modal-actions">
					<Button color="primary" onClick={cancelBooking} data-testid="cancel-booking-confirmation-button">
						{t('Bookings.Cancellation.ImSure')}
					</Button>
					<Button color="primary" onClick={takeMeBack}>
						{t('Bookings.Cancellation.TakeMeBack')}
					</Button>
				</div>
			);
		} else {
			return (
				<div className="canceling-modal-status">
					<i title={t('Bookings.Cancellation.CancelingReservation')} className="fa fa-spin fa-spinner" />
					<span>{t('Bookings.Cancellation.Canceling')}</span>
				</div>
			);
		}
	};

	const getFailedFooter = () => (
		<div className="canceling-modal-status">
			<span>{t('Bookings.Cancellation.Problem')}</span>
			<br />
			<span>
				{t('Bookings.Cancellation.ContactSupport')}{' '}
				<a href={`mailto:${t('HelpEmail')}`} target="_blank" rel="noreferrer">
					{t('SupportTeam')}
				</a>
				.
			</span>
			<br />
			<Button color="primary" onClick={closeCancelModal}>
				{t('Bookings.Cancellation.OK')}
			</Button>
		</div>
	);

	const cancellationText = useMemo(
		() =>
			_.get(booking, 'deal.dca.cancellationPoliciesMultiLanguageTexts')?.[i18n.language] ||
			_.get(booking, 'deal.dca.cancellationPoliciesText'),
		[booking, i18n.language],
	);

	return (
		<MaterialDesignDialog
			open={open}
			onClose={dismissDialog}
			closeTimeoutInMilliseconds={150}
			title={t('Bookings.Cancellation.Title')}
			content={
				<React.Fragment>
					<div className="cancel-reservation-modal-content" data-testid="cancel-reservation-modal-content">
						<div className="description">
							{cancellationText && (
								<span style={in_cancel_policy ? {} : { color: 'red', fontWeight: 'bold' }}>
									{in_cancel_policy
										? t('Bookings.Cancellation.MayIncurCosts')
										: t('Bookings.Cancellation.WillIncurCosts')}
								</span>
							)}
						</div>
						<div className="cancellation-policy">{cancellationText}</div>
					</div>
					{booking.cancelStatus !== ReservationConstants.STATUS.FAILED ? getFooter() : getFailedFooter()}
				</React.Fragment>
			}
		/>
	);
};

export default CancelReservationDialog;
